import { RouterRootState } from 'connected-react-router';
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { toast } from 'react-toastify';
import { useDialog } from '../../../components/dialog/DialogContext';
import { Topic } from '../../../components/item-content-two/Topic';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import TableTab from '../../../components/tabs/table-tab';
import { ZoneCard } from "../../../components/zone/ZoneCard";
import { ZoneDetails } from "../../../components/zone/ZoneDetails";
import { DEF_PG_SIZE_SUB } from '../../../constants/maximum-page-size';
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { useLocale } from "../../../contexts/localeContext";
import { TableContextProvider } from '../../../contexts/table/TableContextProvider';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import doorColumns from '../../../models/ColumnDefinitions/DoorColumns';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { ListData } from '../../../models/ListData';
import { ListQuery } from '../../../models/ListQuery';
import { Lookup } from '../../../models/Lookup';
import LookupTypes from '../../../models/LookupTypes';
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { TabSummary } from '../../../models/TabSummary';
import { Zone as ZoneModel } from '../../../models/Zone';
import doorSearch from '../../../models/search/DoorSearch';
import SiteSearch from '../../../models/search/SiteSearch';
import { hideColumns } from "../../../models/utilities/columnHelper";
import { getControllerNamesWithPagnation } from "../../../services/controllerService";
import { getDoorsForAZone } from '../../../services/doorService';
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { getZoneById, getZoneNamesWithPagination, storeZone } from '../../../services/zoneService';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware/zones';
const itemPath = '/access-control/hardware/zone/';

const initialItem : ZoneModel = {
    id: 0,
    name: '',
    controller: '',
    antiPassback: '',
    site: '',
    doors: 0,
    entryLockoutDelay: 0,
    exitLockoutDelay: 0,
    maxCapacity: 0,
    relaxedTimeExit: undefined,
    apbKey: '',
    zoneNo: 0
}

const Zone: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
        { label: translate.byKey(TranslationKeys.doors), badge: 0, showInitial: false, isActive: true },
    ];
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<ZoneModel>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>(initialTabs)
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [formikContext, setFormikContext] = useState<any>();
    const dialog = useDialog(); 
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);

    useEffect(() => {
        setLookup(compileLookup());
    }, [])

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
        if (itemId){
            dataRefresh((listParameters) => getDoorsForAZone(itemId, listParameters));
        }
    }, [itemId])

    const compileLookup = () => {
        const newLookup = [{
            name: LookupTypes.zoneSite,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: SiteSearch.find(item => item.accessor === "name")?.searchOnName
        },
        {
            name: LookupTypes.zoneController,
            values: [],
            dataMethod: getControllerNamesWithPagnation,
            searchAccessor: 'devName'
        },];
        return newLookup;
    };

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        return newTabs;
    }

    const reloadPage = async () => {
        try {
            let zone;

            if (itemId) {
                zone = await getZoneById(itemId, translate);

                setSelectedItem({label: zone.name, value: zone.id.toString(), isDefault: zone.default})
                setCurrentItem(zone);
                const updatedTabs = updateTabBadgeTotal(1, zone?.doors, initialTabs);
                setTabs(updatedTabs);
            }
            else {
                setCurrentItem(initialItem);
                setTabs([initialTabs[0]]);
            }
            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.zones) },
                { value: itemPath + `${itemId}`, displayName: `${zone?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        }
    }

    const topic: Topic = {
        accessor: 'zoneName',
        title: translate.byKey(TranslationKeys.zones),
        allItemsPath: allItemsPath,
        itemPath: itemPath
    }

    // default to a method that returns nothing
    // when putting a method in a state variable, useState requires us to return the method from a method, hence the wrapped inner method.
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }
    // <-- end context

    const tabContentList: ContentWrapper[] = [{
        content: <ZoneDetails />
    },
    {
        content: <TableContextProvider
            dataMethod={(listParameters) => getDoorsForAZone(itemId, listParameters)}
            tableDescriptor={hideColumns(doorColumns, ['zone', 'entryReader', 'exitReader'])}
            searchDescriptor={doorSearch}
            useTable
            usePagination
            useSearch
            useSort
            useFilter
            manualDataRefresh={manualDataRefresh}
            maxPageSize={DEF_PG_SIZE_SUB}
            lookup={lookup}>
            <TableTab
                title={translate.byKey(TranslationKeys.doors)}
                selectRows={false} 
                cyName="zone"/>
        </TableContextProvider>
    }];

    const getTabContent = (index?: number) => {
        if (itemId && tabs[index ?? 0].isActive) {
            const selectedTabContent = tabContentList[index ?? 0];
            return selectedTabContent.content;
        }
        return tabContentList[0].content;
    };

    const saveZone = (values) => {
        const newZone: ZoneModel = {
            ...currentItem,
            id: +itemId,
            name: values.name,
            controllerId: values.controllerId,
            apbKey: values.apbKey,
            entryLockoutDelay: !values.entryLockoutDelay || values.entryLockoutDelay < 0 ? 0 : values.entryLockoutDelay,
            exitLockoutDelay: !values.exitLockoutDelay || values.exitLockoutDelay < 0 ? 0 : values.exitLockoutDelay,
            maxCapacity: !values.maxCapacity || values.maxCapacity < 0 ? 0 : values.maxCapacity,
            relaxedTimeExit: values.relaxedTimeExit === true || values.relaxedTimeExit === 1 ? true : false,
            commonZoneId: values.commonZoneId,
            nestedZoneId: values.nestedZoneId
        };

        const isNew: boolean = itemId ? false : true;
        saveItem(newZone, isNew);
    }

    const saveItem = (item, createNewItem) => {
        setBusy(true);
        storeZone(item, createNewItem)
            .then(async (value: any) => {
                setCurrentItem(item);
                setItemId(value.id);
                history.push(`${itemPath}${value.id}`);
                toast.success(translate.byKey(TranslationKeys.zoneSaved));
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
                if (formikContext) {
                    formikContext.resetForm();
                }
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <TopicContextProvider itemId={itemId} dataMethod={getZoneNamesWithPagination} topic={topic} selectedItem={selectedItem} >
        <ItemContentTabTwo>
            <SimpleItemDetails
                breadcrumb={breadcrumb}
                busy={busy}
                getTabContent={getTabContent}
                itemProfile={currentItem}
                onBack={() => history.push(allItemsPath)}
                saveDetails={saveZone}
                tabs={tabs}
                itemPath={itemPath}
                initialValues={currentItem}
                children={
                    <ZoneCard setFormikContext={setFormikContext} />} />
        </ItemContentTabTwo>
    </TopicContextProvider>
}

export default Zone;
